'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

export default function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [width, setWidth] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevKey = useRef(`${pathname}${searchParams.toString()}`);

  useEffect(() => {
    const currentKey = `${pathname}${searchParams.toString()}`;
    if (currentKey === prevKey.current) return;
    prevKey.current = currentKey;

    // Navigation completed — finish the bar
    setWidth(100);
    timerRef.current = setTimeout(() => {
      setLoading(false);
      setWidth(0);
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pathname, searchParams]);

  // Expose a start function via a custom event
  useEffect(() => {
    const handleStart = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setLoading(true);
      setWidth(10);

      let current = 10;
      intervalRef.current = setInterval(() => {
        current += Math.random() * 15;
        if (current >= 85) {
          current = 85;
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
        setWidth(current);
      }, 200);
    };

    window.addEventListener('nav:start', handleStart);
    return () => {
      window.removeEventListener('nav:start', handleStart);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (!loading && width === 0) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none"
      style={{ opacity: loading || width < 100 ? 1 : 0, transition: 'opacity 0.3s ease' }}
    >
      <div
        className="h-full bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]"
        style={{
          width: `${width}%`,
          transition: width === 100 ? 'width 0.2s ease-out' : 'width 0.2s ease',
        }}
      />
    </div>
  );
}
